/**
 * Swap-collateral holdings for leveraged (2x) funds.
 *
 * Leveraged funds hold a TRS (total-return swap) on the underlying plus
 * money-market / treasury collateral and a cash balancing line. Those rows are
 * served by the same public holdings endpoint as equity funds
 * (`GET /api/data/holdings?ticker=X`, Postgres-backed); this hook fetches them
 * and maps the raw rows into the `LeveragedRow` shape the leveraged holdings
 * table renders. Returns `rows: null` (→ "Holdings unavailable." state) when a
 * fund has no ingested snapshot yet (e.g. swap-based single-stock funds).
 */
import { apiFetch } from "@/lib/api-client";
import { useQuery } from "@tanstack/react-query";

export interface LeveragedRow {
  name: string;
  typeLabel: string;
  weightPct: number;
  marketValue: number;
  highlight?: boolean;
}

// Raw row shape from the holdings API (shared with use-etf-live-holdings).
interface HoldingRow {
  security_name: string;
  security_cusip: string;
  security_ticker: string;
  weight_pct: string | number | null;
  market_value: string | number | null;
  shares: string | number | null;
}

const SWAP_RE = /(?:total[\s-]*return|tr)[\s-]*swap|\bt\.?r\.?s\b|\bswap\b/i;
const COLLATERAL_RE =
  /government\s+obligation|gov.?t\s+obligation|money\s*market|treasur|t-?bill|first\s+american/i;
const CASH_RE = /\bcash\b/i;

/** Short instrument-type label for the leveraged holdings CSV / "Type" column. */
function leveragedTypeLabel(name: string): string {
  if (SWAP_RE.test(name)) return "TRS";
  if (COLLATERAL_RE.test(name)) return "T-Bill";
  if (CASH_RE.test(name)) return "Cash";
  return "";
}

function toNum(v: string | number | null): number {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function useLeveragedHoldings(ticker: string): {
  rows: LeveragedRow[] | null;
  asOf: string | null;
  loading: boolean;
} {
  const { data, isPending } = useQuery({
    // Same key + endpoint as useLiveHoldings → one shared request per ticker.
    queryKey: ["holdings", ticker],
    queryFn: () => apiFetch<HoldingRow[]>(`/api/data/holdings?ticker=${ticker}`),
    enabled: Boolean(ticker),
    staleTime: 0,
    retry: false,
  });

  if (!data || data.length === 0) {
    return { rows: null, asOf: null, loading: isPending };
  }

  const rows: LeveragedRow[] = data.map((r) => ({
    name: r.security_name,
    typeLabel: leveragedTypeLabel(r.security_name),
    weightPct: toNum(r.weight_pct),
    marketValue: toNum(r.market_value),
    // Highlight the swap line — it carries the fund's leveraged exposure.
    highlight: SWAP_RE.test(r.security_name),
  }));

  // The holdings endpoint returns no date column; the table falls back to the
  // caller-supplied `fallbackDateLabel` (the live daily_nav/holdings date).
  return { rows, asOf: null, loading: false };
}
